import { callZapier } from 'utils/zapier/callZapier'
import { useState, useContext, useMemo, useEffect, useCallback } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import { useSearchParams } from 'react-router-dom'
import WizardStep from 'components/WizardSteps/WizardStep'
import { useFinances } from 'hooks/useFinances'

export const useOnboarding = ({
  steps,
  updateUrlOnNavigation = true,
  transitionTimeout = 250,
  customHandleNext,
}) => {
  const {
    updateUser,
    intakeState,
    nonUserState,
    setNonUserState,
    updateIntakeStateAtKey,
    currentErrors,
  } = useContext(IntakeContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const financesClient = useFinances()

  // register any hooks you need for onboarding steps
  const onboardingHooks = useMemo(
    () => ({
      financesClient,
      updateIntakeStateAtKey,
      setNonUserState,
    }),
    [intakeState, financesClient, updateIntakeStateAtKey, setNonUserState]
  )

  const [activeStep, setActiveStep] = useState(
    Math.max(
      steps.findIndex(({ id }) => id === searchParams.get('onboardingStep')),
      0
    )
  )

  const [direction, setDirection] = useState('forward') // 'forward' or 'backward'
  const isLastStep = activeStep === steps.length - 1

  const [skippedSections, setSkippedSections] = useState([])

  const setParamsForOnboarding = useCallback(() => {
    const paramToPopulate = steps[activeStep]?.id || steps[0].id
    if (paramToPopulate === searchParams.get('onboardingStep')) return
    setSearchParams({
      onboardingStep: steps[activeStep]?.id || steps[0].id,
    })
  }, [activeStep, searchParams, setSearchParams, steps])

  useEffect(() => {
    if (updateUrlOnNavigation) {
      setParamsForOnboarding()
    }
  }, [direction, setParamsForOnboarding])

  function questionCheck(dependency) {
    // OR group
    if (dependency.or) {
      return dependency.or.some(questionCheck)
    }

    // AND group
    if (dependency.and) {
      return dependency.and.every(questionCheck)
    }

    // Single condition
    if (typeof dependency.value === 'function') {
      return dependency.value(intakeState[dependency.attribute])
    }

    return dependency.inverse
      ? intakeState[dependency.attribute] !== dependency.value
      : intakeState[dependency.attribute] === dependency.value
  }

  const currentQuestionValue = intakeState[steps[activeStep]?.props?.id]
  if (
    //check dependencies and inverse dependencies
    (steps[activeStep]?.dependencies || []).every(questionCheck)
  ) {
    // console.log('good to go on the next step')
  } else {
    // console.log('YOU HAVE UNMET DEPENDENCIES')
    setActiveStep(direction === 'forward' ? activeStep + 1 : activeStep - 1)
  }

  const userNotification = useCallback(
    (message) => {
      callZapier({
        user: intakeState,
        hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/38fqdg5/',
        payload: {
          user: {
            firstName: intakeState.firstName,
            lastName: intakeState.lastName,
            phoneNumber: intakeState.phone,
            email: intakeState.email,
            message,
          },
        },
        sendNotification: false,
      })
    },
    [intakeState]
  )

  const leadNotification = useCallback(
    (message) => {
      callZapier({
        user: intakeState,
        hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2rt60e6/',
        payload: {
          user: intakeState,
          message: message,
        },
        sendNotification: false,
      })
    },
    [intakeState]
  )

  const createNewLead = () => {
    if (intakeState?.email === null) return
    if (intakeState?.phone === null) return
    if (intakeState?.lastName === '' || intakeState?.lastName === null) return
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2tb7wuh/',
      payload: {
        user: intakeState,
      },
      sendNotification: false,
    })
  }

  const isDisabled =
    steps[activeStep]?.required &&
    (currentQuestionValue === null ||
      currentQuestionValue === undefined ||
      currentQuestionValue?.trim() === '')

  const handleNext = useCallback(async () => {
    if (isDisabled) return

    window.scrollTo({ top: -100, behavior: 'smooth' })

    const nextStepActions = async () => {
      let updatedState

      if (steps[activeStep]?.props?.handleNextOverride) {
        updatedState = await steps[activeStep].props.handleNextOverride(
          intakeState,
          onboardingHooks,
          nonUserState,
          setActiveStep,
          activeStep
        )
      }
      if (steps[activeStep]?.props?.inputType !== 'displayOnly') {
        const success = await updateUser(updatedState?.intakeState)
        // connect to FEHB question
        // const idFromStep = steps[activeStep]?.id
        // const sendToZapier = (id, intakeState) => {
        //   if (
        //     id === 'healthcareTileSelector' &&
        //     (intakeState?.healthInsurance != 'fehb' || null)
        //   ) {
        //     callZapier({
        //       user: intakeState,
        //       hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/3fc8zwq/',
        //       payload: {
        //         user: {
        //           firstName: intakeState.firstName,
        //           lastName: intakeState.lastName,
        //           phoneNumber: intakeState.phone,
        //           email: intakeState.email,
        //           healthInsurance: intakeState.healthInsurance,
        //         },
        //       },
        //       sendNotification: false,
        //     })
        //   }
        // }
        // sendToZapier(idFromStep, intakeState)
      }

      // if handle next override needs to skip a step return false and adjust the step there
      if (updatedState !== false) {
        setActiveStep(activeStep + 1)
      }
      setDirection('forward')
    }
    // setTimeout(async () => {
    //   await nextStepActions()
    // }, transitionTimeout)

    await nextStepActions()

    setDirection('forward')
    setAnimate(true)

    if (activeStep === 6 && window.location.search !== '') {
      userNotification('New User has just been created')
    }
    if (activeStep === 6 && intakeState.salesforceLeadID === null) {
      leadNotification('New Lead Onboarding in the System')
    }
    if (activeStep === 0) {
      createNewLead()
    }
  }, [
    activeStep,
    intakeState,
    isDisabled,
    steps,
    updateUser,
    userNotification,
    leadNotification,
    onboardingHooks,
  ])

  const handleBack = useCallback(() => {
    window.scrollTo({ top: -100, behavior: 'smooth' })

    setTimeout(() => {
      const nextStep = activeStep - 1
      if (nextStep < 0) return setActiveStep(0)

      const matchingSection = skippedSections.find(({ start, end }) => {
        const isNextInSection = nextStep >= start && nextStep <= end
        const isNotAlreadyInside = !(activeStep >= start && activeStep <= end)

        return isNextInSection && isNotAlreadyInside
      })

      if (matchingSection) {
        setActiveStep(matchingSection.start)
      } else {
        setActiveStep(nextStep)
      }
    }, 0)

    setDirection('backward')
    setAnimate(true)
  }, [activeStep, skippedSections])

  const handleSkipSection = useCallback(() => {
    const startIndex = activeStep
    let endIndex = steps.length - 1

    for (let i = activeStep + 1; i < steps.length; i++) {
      const step = steps[i]
      const isNextInterstitial = step.id?.startsWith('interstitial')
      const meetsDeps = (step.dependencies || []).every(questionCheck)

      if (isNextInterstitial && meetsDeps) {
        endIndex = i - 1
        break
      }
    }

    setSkippedSections((prev) => [
      ...prev,
      { start: startIndex, end: endIndex },
    ])

    const nextStep = endIndex + 1
    setAnimate(true)
    setActiveStep(nextStep)
    setDirection('forward')
    setAnimate(true)
  }, [activeStep, steps, intakeState])

  const {
    component: StepComponent = WizardStep,
    props,
    required = false,
  } = useMemo(() => {
    if (steps[activeStep]) {
      return steps[activeStep] || WizardStep
    }

    return {}
  }, [activeStep, steps])

  const [animate, setAnimate] = useState(false)

  const additionalContext = steps[activeStep]?.additionalContext

  const customHandleNextWithActiveStepSetting = () => {
    customHandleNext()
    setActiveStep(activeStep + 1)
    setDirection('forward')
    setAnimate(true)
  }

  return {
    activeStep,
    animate,
    setAnimate,
    direction,
    userNotification,
    StepComponent,
    stepComponentProps: props,
    required,
    handleBack,
    handleNext: customHandleNext
      ? customHandleNextWithActiveStepSetting
      : handleNext,
    handleSkipSection,
    isLastStep,
    isDisabled,
    additionalContext,
    intakeState,
  }
}
